import React, { createContext, forwardRef, useContext, useMemo } from "react";
import type { FieldValues } from "react-hook-form";

import type { GroupBase, SelectProps } from "@braze/beacon-lab-select";

import { BrazeSelect } from "@components/BrazeSelect";
import type { BrazeSelectOperatorSearchProps } from "@components/BrazeSelect";
import { SelectField } from "@components/ReactHookForm";

import type { BrazeSelectFieldProps, OperatorSelectComponent, OperatorSelectContextValue } from "./types";

const OperatorSelectContext = createContext<OperatorSelectContextValue | undefined>(undefined);

const OperatorSelect = forwardRef(
  <OptionType, IsMulti extends boolean = false, GroupType extends GroupBase<OptionType> = GroupBase<OptionType>>(
    componentProps: SelectProps<OptionType, IsMulti, GroupType>,
    ref: React.ForwardedRef<unknown>
  ) => {
    const operatorProps = useContext(OperatorSelectContext);

    if (!operatorProps) {
      throw new Error("OperatorSelect must be rendered within an OperatorSelectContext provider");
    }

    const { onOperatorSearch, ...restOperatorProps } = operatorProps;

    return (
      <BrazeSelect
        {...componentProps}
        {...restOperatorProps}
        ref={ref}
        onOperatorSearch={onOperatorSearch as BrazeSelectOperatorSearchProps<OptionType>["onOperatorSearch"]}
      />
    );
  }
) as OperatorSelectComponent;
OperatorSelect.displayName = "BrazeSelectFieldSelect";

/**
 * Operator-controllable react-hook-form select field.
 *
 * Mirrors `BrazeInputField`: it reuses the shared `SelectField`'s value/option-matching and
 * react-hook-form wiring, but injects `BrazeSelect` (via `SelectField`'s `Component` prop) so the
 * rendered control registers Operator tools and exposes `operatorLabel`. Use this anywhere a raw
 * `<SelectField>` is rendered on an Operator-enabled page — do not leave the field uninstrumented
 * because it lives in a shared `@components/*` wrapper.
 */
export const BrazeSelectField = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
  FormValues extends FieldValues = FieldValues,
>({
  operatorLabel,
  operatorDisabledReason,
  operatorToolId,
  onOperatorSearch,
  ...rest
}: BrazeSelectFieldProps<OptionType, IsMulti, GroupType, FormValues>): React.ReactElement => {
  const operatorProps = useMemo(() => {
    return { operatorLabel, operatorDisabledReason, operatorToolId, onOperatorSearch };
  }, [operatorLabel, operatorDisabledReason, operatorToolId, onOperatorSearch]);

  return (
    <OperatorSelectContext.Provider value={operatorProps}>
      <SelectField<OptionType, IsMulti, GroupType, FormValues> {...rest} Component={OperatorSelect} />
    </OperatorSelectContext.Provider>
  );
};
